import { EU_TARIFF_TYPES } from '@/components/App/NewOrder/constants';
import { keysToCamelCase, validateNumberInputValue } from '@/services';
import { formatPhoneNumber } from '@/services/formatter';
import type {
  TAutoCredLeasingRequest,
  TAutoCredLeasingRequestApi,
  TNewOrder
} from '@/types/newOrder';
import type { TPersonSpouseInfo } from '@/types/personInfo';
import type { TFile, TRequestDetailed } from '@/types/request';

const EU_TARIFF_TYPES_ARRAY = [
  EU_TARIFF_TYPES.STANDARD,
  EU_TARIFF_TYPES.INSTALLMENT,
  EU_TARIFF_TYPES.SUBSIDY,
  EU_TARIFF_TYPES.PREFERENTIAL
];

export const orderAppMapper = (request: TRequestDetailed): TNewOrder => {
  const requestFiles = [...request.Files].sort((a, b) => {
    return (
      new Date(b.create_date).getTime() - new Date(a.create_date).getTime()
    );
  });

  const clientPhotoHandler = requestFiles.find(
    (file) =>
      file.type === 'clientBiometricPhoto' || file.type === 'clientPhoto'
  );
  const clientPhoto: TFile = {
    link: clientPhotoHandler?.link?.replace('https', 'http') || '',
    create_date: clientPhotoHandler?.create_date || '',
    name: clientPhotoHandler?.name || '',
    request_uuid: clientPhotoHandler?.request_uuid || '',
    type: clientPhotoHandler?.type || '',
    uuid: clientPhotoHandler?.uuid || ''
  };
  const docScanSocialStatus = requestFiles.find(
    (file) => file.type === 'docScanSocialStatus'
  );
  const docPhoto = requestFiles.find((file) => file.type === 'docPhoto');
  const proofOfIncome = requestFiles.find((file) => file.type === 'incomeCert');
  const noticeActivity = requestFiles.find(
    (file) => file.type === 'noticeActivity'
  );
  const otherDoc = requestFiles.find((file) => file.type === 'otherDoc'); //Справка за 6 месяцев
  const stockbondsArrayHandler = request.RequestsProducts.filter(
    (reqProd) => reqProd.product.product_type.code !== 2
  );
  const addProductsArrayHandler = request.RequestsProducts.filter(
    (reqProd) => reqProd.product.product_type.code === 2
  );

  const orderState: TNewOrder = {
    sentTo:
      request.sent_to ||
      (request.is_online_request ? 'banks' : 'banks_leasing'),
    Requests_Comments: request.Requests_Comments,
    biometricSession: request?.biometric_session
      ? {
          isExpired: request?.biometric_session.last_event_name === 'expired',
          isLoading:
            request?.biometric_session.last_event_name !== 'expired' &&
            request?.biometric_session.last_event_name !== 'flow.end',
          isValidated: request?.biometric_session.is_validated
        }
      : null,
    code: request.code,
    createDate: request.create_date,
    isOnlineRequest: !!request.is_online_request,
    onlineClientRequest: keysToCamelCase(
      request.Online_Client_Requests?.[0] || null
    ),
    otp: request.otp,
    otpDate: request.otp_create_date,
    status: request.status.code || '',
    uuid: request.uuid,
    productInfo: {
      additionalProducts: addProductsArrayHandler.map((reqProd) => {
        const currentValue = JSON.parse(reqProd.current_value);
        return {
          bank_name: reqProd?.product?.bank?.name || '',
          checked: true,
          code: reqProd?.product?.code || '',
          grace: reqProd?.product?.grace_period
            ? Number(reqProd?.product?.grace_period)
            : null,
          id: reqProd?.uuid || '',
          info: reqProd?.product?.promo_body || '',
          label: reqProd?.product?.name || '',
          loan_amount: reqProd?.product?.loan_amount || null,
          loan_period: currentValue?.loan_period || null,
          partner_code: reqProd?.product?.partner_code || null,
          product_code: reqProd?.product?.product_code || null,
          value: reqProd?.product?.bank?.name || ''
        };
      }),
      product: {
        // autoColor: request?.color?.code || '2', //Черный цвет
        autoType: request?.auto_type?.code || '1', //Типа автомобиля
        eurasianTariffType:
          EU_TARIFF_TYPES_ARRAY[request.eubank_tariff_type ?? 0] ||
          EU_TARIFF_TYPES.STANDARD,
        carYear:
          request?.car_year?.toString() || new Date().getFullYear().toString(),
        chosenDealer:
          request?.chosen_dealer_uuid || Number(request?.dealer?.code),
        costCar:
          validateNumberInputValue(
            request?.vehicle_cost?.toString(),
            10,
            true
          ) || '',
        dealer: request?.dealer?.code || '',
        engineType: request?.engine_type?.code || '1', //Бензиновый
        initFee:
          validateNumberInputValue(request?.init_fee?.toString(), 10, true) ||
          '',
        loanTerm: request?.loan_period_code || request?.loan_period || '',
        markModelYear: request?.model
          ? {
              brand: Number(request?.model?.brand?.code) || '',
              equipment: request?.equipment_uuid,
              model: Number(request?.model?.code) || '',
              year: request?.car_year || new Date().getFullYear()
            }
          : null,
        usedCar: request?.used_car,
        // paymentDate: request?.payment_date || '',
        paymentScheme: request?.repayment_type?.code || '1', //Код ануетета
        proofInCome: request.is_proof_income,
        requestAmount:
          validateNumberInputValue(
            request?.request_amount?.toString(),
            10,
            true
          ) || '',
        stocksBonds: stockbondsArrayHandler.map((reqProd) => ({
          bank_name: reqProd?.product?.bank?.name || '',
          checked: true,
          code: reqProd?.product?.code || '',
          grace: reqProd?.product?.grace_period
            ? Number(reqProd?.product?.grace_period)
            : null,
          id: reqProd?.uuid || '',
          info: reqProd?.product?.promo_body || '',
          label: reqProd?.product?.name || '',
          loan_amount: reqProd?.product?.loan_amount || null,
          loan_period: reqProd?.product?.loan_period || null,
          partner_code: reqProd?.product?.partner_code || null,
          product_code: reqProd?.product?.product_code || null,
          value: reqProd?.product?.bank?.name || ''
        }))
      }
    },
    personalInfo: {
      addContactsFullName:
        request?.client?.Client_Add_Contacts[0]?.full_name || '',
      addContactsIsRelated:
        request?.client?.Client_Add_Contacts[0]?.add_contacts_is_related?.code?.toString() ||
        '',
      addContactsTelNum:
        request?.client?.Client_Add_Contacts[0]?.phone_number || '',
      additionalSalary: `${request?.client?.additional_income || ''}`,
      children: `${request?.client?.child_count || '0'}`,
      clientIin: request?.client?.client_iin || '',
      companyName: request?.client?.title_company || '',
      companyType: request?.client?.organization_type?.code?.toString() || '',
      dateBirth: request?.client?.birth_date
        ? new Date(request?.client?.birth_date || '').toString()
        : '',
      docType: String(request?.client?.document_type?.code || ''),
      familyStatus: String(request?.client?.marital_status?.code || ''),
      firstName: request?.client?.first_name || '',
      isClientPhoneNumberVerified:
        request?.client?.is_phone_number_verified ?? true,
      jobAddress: request?.client?.work_address || '',
      jobTitle: request?.client?.job_title || '',
      lastName: request?.client?.last_name || '',
      middleName: request?.client?.middle_name || '',
      phoneNumber: formatPhoneNumber(request?.client?.phone_number),
      salary: `${request?.client?.salary || ''}`,
      sex: String(request?.client?.gender?.code || ''),
      socialStatus: request?.client?.social_status?.code?.toString() || '0',
      workTelNumber: formatPhoneNumber(request?.client?.work_tel_number),
      spouseInfo:
        request?.client?.Spouses && request?.client?.Spouses?.length !== 0
          ? ({
              isMarried: true,
              spouseAddSalary:
                request?.client?.Spouses[0]?.additional_income || '',
              spouseCompanyName: request?.client?.Spouses[0]?.workplace || '',
              spouseDateBirth:
                request?.client?.Spouses[0]?.birth_date.toString() || '',
              spouseFullName: request?.client?.Spouses[0]?.full_name || '',
              spouseIin: request?.client?.Spouses[0]?.iin || '',
              spousePlaceBirth: request?.client?.Spouses[0]?.birthplace || '',
              spouseSalary: request?.client?.Spouses[0]?.salary || '',
              spouseTelNum: request?.client?.Spouses[0]?.mobile_phone || ''
            } as TPersonSpouseInfo)
          : {
              isMarried: false,
              spouseAddSalary: '',
              spouseCompanyName: '',
              spouseDateBirth: null,
              spouseFullName: '',
              spouseIin: '',
              spousePlaceBirth: '',
              spouseSalary: '',
              spouseTelNum: ''
            }
    },
    additionalInfo: {
      files: {
        clientPhoto: clientPhoto,
        docPhoto: docPhoto ? docPhoto : null,
        docScanSocialStatus:
          request?.client?.social_status?.code?.toString() === '3' && // Военнослужащий
          docScanSocialStatus
            ? docScanSocialStatus
            : null,
        incomeCert:
          request.is_proof_income && proofOfIncome ? proofOfIncome : null,
        noticeActivity:
          request?.client?.organization_type?.code?.toString() === '1' && // ИП
          noticeActivity
            ? noticeActivity
            : null,
        otherDoc: otherDoc ? otherDoc : null
      } // Files
    }
  };
  return orderState;
};

export const orderAppLeasingMapper = (
  request: TAutoCredLeasingRequestApi
): TAutoCredLeasingRequest => {
  return keysToCamelCase(request);
};
